import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Sparkles, Upload, Wand2, Share2, ArrowRight, Shirt } from "lucide-react";
import { api, promptImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DropCountdown } from "@/components/DropCountdown";

const IMG = "https://static.prod-images.emergentagent.com/jobs/0b39994e-7b47-4c7c-b811-ab6357de0f51/images/";
const HERO = [
  { src: `${IMG}56f64ffdcd224a6f7de2c329869b3b86093b660b8c7be207ff75590275dfd05b.jpeg`, label: "Cafe Barista", r: "-6deg", c: "#FFC72C" },
  { src: `${IMG}6824c44ff32c5f6c9d16db5ba85acc65c0df2bf118294f83cd2b0a520bc72b58.jpeg`, label: "Renaissance Royal", r: "5deg", c: "#36D399" },
  { src: `${IMG}26d9f3b33f0ce0615547f7e89e0899710e09a275439354a493585fd04163ef93.jpeg`, label: "Astronaut Explorer", r: "-3deg", c: "#00C4EE" },
];

const STEPS = [
  { icon: Upload, title: "Upload your cutie", text: "Snap a clear photo of your dog, cat, bunny or lizard. We keep it private." },
  { icon: Wand2, title: "Pick a viral style", text: "Renaissance royalty, astronaut, cyberpunk, 90s yearbook and more." },
  { icon: Share2, title: "Share, save, print", text: "Download in HD, post to the community feed, or order it on a mug." },
];

export default function Landing() {
  const { user } = useAuth();
  const [prompts, setPrompts] = useState([]);
  useEffect(() => { api.get("/prompts").then((r) => setPrompts(r.data)).catch(() => {}); }, []);
  const cta = user ? "/studio" : "/signup";

  return (
    <main data-testid="landing-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20 lg:pt-20 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 rise">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#FFF0F3] text-[#E04362] px-3 py-1 text-xs font-bold uppercase tracking-wider border border-[#FF5A79]/30">
            <Sparkles className="h-3.5 w-3.5" /> 5 free creations every day
          </span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            Your pet, but make it <span className="relative inline-block text-[#FF5A79]">iconic<svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none"><path d="M2 9C50 3 150 3 198 9" stroke="#FFC72C" strokeWidth="5" strokeLinecap="round" /></svg></span>.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#6E687A] max-w-lg leading-relaxed">
            Upload a photo of your furry best friend and Cutie Parlor's AI turns them into royal portraits, space explorers, anime stars and every viral trend in between.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to={cta} data-testid="hero-cta-button" className="inline-flex items-center gap-2 rounded-full bg-[#FF5A79] text-white sticker sticker-press px-7 py-3.5 font-bold text-base">
              Create my pet's glow-up <ArrowRight className="h-5 w-5" />
            </Link>
            <Link to="/community" data-testid="hero-community-button" className="inline-flex items-center gap-2 rounded-full bg-white sticker sticker-press px-7 py-3.5 font-bold text-base">
              See the feed
            </Link>
          </div>
        </div>
        <div className="lg:col-span-6 relative h-[420px] sm:h-[480px]">
          <div className="absolute -top-6 right-10 h-40 w-40 rounded-full bg-[#8A4FFF]/20 blur-3xl" />
          <div className="absolute bottom-0 left-4 h-48 w-48 rounded-full bg-[#FFC72C]/30 blur-3xl" />
          {HERO.map((h, i) => (
            <figure key={h.label} className="absolute floaty rounded-3xl overflow-hidden bg-white sticker p-2"
              style={{ "--r": h.r, animationDelay: `${i * 0.8}s`, width: i === 1 ? "52%" : "44%",
                top: ["8%", "0%", "46%"][i], left: ["0%", "44%", "26%"][i] }}>
              <img src={h.src} alt={h.label} className="w-full aspect-square object-cover rounded-2xl" />
              <figcaption className="mt-2 mb-1 px-2 flex items-center justify-between text-sm font-bold">
                {h.label}<span className="h-3 w-3 rounded-full" style={{ background: h.c }} />
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="border-y-2 border-[#1E1B26] bg-[#1E1B26] text-white overflow-hidden py-4" data-testid="viral-marquee">
        <div className="flex gap-10 whitespace-nowrap marquee w-max font-display text-2xl font-semibold">
          {[...prompts, ...prompts].map((p, i) => (
            <span key={`${p.id}-${i < prompts.length ? "a" : "b"}`} className="flex items-center gap-10">{p.title}<Sparkles className="h-5 w-5 text-[#FFC72C]" /></span>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight max-w-xl">Three steps from couch potato to cover star.</h2>
        <div className="mt-14 grid md:grid-cols-3 gap-8">
          {STEPS.map((s, i) => (
            <div key={s.title} className="rise rounded-3xl bg-white border border-[#EAE3D9] p-8" style={{ animationDelay: `${i * 120}ms` }}>
              <div className="h-12 w-12 rounded-2xl bg-[#F3EFFF] grid place-items-center"><s.icon className="h-6 w-6 text-[#8A4FFF]" /></div>
              <p className="mt-6 font-mono text-xs text-[#6E687A]">0{i + 1}</p>
              <h3 className="mt-1 text-xl font-semibold">{s.title}</h3>
              <p className="mt-3 text-[#6E687A] leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">This week's viral styles</h2>
          <Link to={cta} className="font-bold text-[#FF5A79] inline-flex items-center gap-1" data-testid="styles-see-all">Try them all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="mt-6"><DropCountdown newCount={prompts.filter((p) => p.is_new).length} compact /></div>
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {prompts.slice(0, 8).map((p) => (
            <Link to={cta} key={p.id} data-testid={`landing-style-${p.id}`} className="group relative rounded-3xl overflow-hidden bg-white border border-[#EAE3D9] hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300">
              {p.is_new && <span className="absolute z-10 top-3 right-3 rounded-full bg-[#FF5A79] text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider sticker rotate-3">New</span>}
              <div className="aspect-[4/5] overflow-hidden"><img src={promptImg(p)} alt={p.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
              <div className="p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#8A4FFF]">{p.tag}</p>
                <p className="font-display text-lg font-semibold">{p.title}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="rounded-[2rem] bg-[#8A4FFF] text-white sticker p-10 sm:p-14 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Shirt className="h-10 w-10 text-[#FFC72C]" />
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold">Wear it. Sip from it. Frame it.</h2>
            <p className="mt-4 text-white/80 leading-relaxed">Turn any creation into a mug, tee or poster printed on demand and shipped to your door.</p>
          </div>
          <div className="flex md:justify-end">
            <Link to={cta} data-testid="merch-cta-button" className="inline-flex items-center gap-2 rounded-full bg-[#FFC72C] text-[#1E1B26] sticker sticker-press px-7 py-3.5 font-bold">Start creating <ArrowRight className="h-5 w-5" /></Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#EAE3D9] py-10 text-center text-sm text-[#6E687A]">© {new Date().getFullYear()} Cutie Parlor. Made with treats.</footer>
    </main>
  );
}
